import { VStack, HStack, Text, Input, Box } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { ChevronDown, Calendar, FileText } from 'lucide-react'
import { CreateUnitMutationInput, getAllMutationTypes } from '../../types/mutations'

interface Step1MutationTypeProps {
  formData: Partial<CreateUnitMutationInput>
  onUpdate: (data: Partial<CreateUnitMutationInput>) => void
}

export function Step1MutationType({ formData, onUpdate }: Step1MutationTypeProps) {
  // Obtenir tous les types de mutation
  const allTypes = getAllMutationTypes()

  // Grouper par catégorie
  const grouped = allTypes.reduce(
    (acc, type) => {
      if (!acc[type.category]) {
        acc[type.category] = []
      }
      acc[type.category]!.push(type)
      return acc
    },
    {} as Record<string, typeof allTypes>
  )

  const categoryLabels: Record<string, string> = {
    onerous: 'Mutations à titre onéreux',
    gratuitous: 'Mutations à titre gratuit',
    matrimonial: 'Mutations liées au régime matrimonial',
    company: 'Mutations liées aux sociétés',
    dismemberment: 'Mutations par démembrement',
    indivision: 'Mutations liées à l\'indivision',
    judicial: 'Mutations judiciaires et administratives',
    specific: 'Mutations spécifiques',
  }

  return (
    <VStack align="stretch" gap={6}>
      <Text fontSize="md" color="gray.600">
        Sélectionnez le type de mutation et la date à laquelle elle a eu lieu.
      </Text>

      {/* Type de mutation - Required */}
      <Field.Root required>
        <Field.Label fontSize="sm" fontWeight="semibold">
          <HStack gap={2}>
            <FileText size={14} />
            <Text>Type de mutation</Text>
          </HStack>
        </Field.Label>
        <NativeSelectRoot position="relative">
          <NativeSelectField
            value={formData.mutation_type || ''}
            onChange={(e) => onUpdate({ mutation_type: e.target.value as any })}
            paddingRight="10"
          >
            <option value="">Sélectionner un type de mutation</option>
            {Object.entries(grouped).map(([category, types]) => (
              <optgroup
                key={category}
                label={categoryLabels[category] || 'Autre'}
              >
                {types.map((type) => (
                  <option key={type.code} value={type.code}>
                    {type.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </NativeSelectField>
          <Box
            position="absolute"
            right="3"
            top="50%"
            transform="translateY(-50%)"
            pointerEvents="none"
            color="gray.500"
          >
            <ChevronDown size={14} />
          </Box>
        </NativeSelectRoot>
        <Field.HelperText fontSize="xs">
          Choisissez le type de mutation correspondant à votre cas
        </Field.HelperText>
      </Field.Root>

      {/* Description du type sélectionné */}
      {formData.mutation_type && (
        <Box
          p={3}
          bg="brand.50"
          borderWidth="1px"
          borderColor="brand.200"
          borderRadius="md"
        >
          <Text fontSize="sm" color="brand.800">
            <strong>Description :</strong>{' '}
            {allTypes.find((t) => t.code === formData.mutation_type)?.description}
          </Text>
        </Box>
      )}

      {/* Date de mutation - Required */}
      <Field.Root required>
        <Field.Label fontSize="sm" fontWeight="semibold">
          <HStack gap={2}>
            <Calendar size={14} />
            <Text>Date de la mutation</Text>
          </HStack>
        </Field.Label>
        <Input
          type="date"
          value={formData.mutation_date || ''}
          onChange={(e) => onUpdate({ mutation_date: e.target.value })}
          max={new Date().toISOString().split('T')[0]}
        />
        <Field.HelperText fontSize="xs">
          Date à laquelle la mutation a pris effet
        </Field.HelperText>
      </Field.Root>

      {/* Sous-type optionnel */}
      <Field.Root>
        <Field.Label fontSize="sm" color="gray.600">
          Précision complémentaire (optionnel)
        </Field.Label>
        <Input
          placeholder="Ex: Succession ab intestat, Donation-partage, etc."
          value={formData.mutation_subtype || ''}
          onChange={(e) => onUpdate({ mutation_subtype: e.target.value })}
        />
        <Field.HelperText fontSize="xs">
          Ajoutez des précisions sur le type de mutation si nécessaire
        </Field.HelperText>
      </Field.Root>
    </VStack>
  )
}
